import { findOne } from 'rcd-cpp-workspace/utils/array';
import * as React from 'react';
import { Dropdown } from '../../Dropdown/dropdown';
import { FilterProps } from '../filters';
import './dateFilter.scss';
import { Op } from '../../../enums/Op';
import { DatePicker } from '../../DatePicker/datePicker';
import {
  formatDate,
  parseDate,
  parseCondition,
  debounce
} from '../../../utils';
import { Options } from '../Options/options';
import { Arrow } from 'rcd-cpp-ui/ui/icon/Arrow';
import { AdminDomain } from '../../../enums/AdminDomain';

interface Condition {
  [op: string]: { from?: string; to?: string } | boolean | string[] | string;
}

const OPTIONS = [
  Op.equals,
  Op.greaterThan,
  Op.lessThan,
  Op.between,
  Op.P7D,
  Op.P30D,
  Op.P90D,
  Op.relative,
  Op.isEmpty,
  Op.isNotEmpty
];
const TODAY = 'TODAY';

const isRelative = (op: Op) =>
  [Op.P7D, Op.P30D, Op.P90D, Op.relative].includes(op);

export const DateFilter = ({ filter, field, opts, setFilter }: FilterProps) => {
  const curFilter = filter; //findOne(filters, { field: filter.field });
  if (!opts) opts = OPTIONS;
  if (curFilter.field.startsWith('PublishStagedTags.')) {
    opts = opts.filter(o => !isRelative(o));
  }
  const { op, val } = parseCondition(curFilter.conditions, opts[0]);
  let startDate: any = val,
    endDate: any = null;
  if (Array.isArray(val)) {
    startDate = parseDate(val[0]);
    endDate = parseDate(val[1]);
  } else if (!val?.from) {
    startDate = parseDate(val);
  }

  const isCheckboxDisabled =
    curFilter?.conditions?.length < 0 ||
    (curFilter?.conditions?.length > 0 &&
      Object.values(curFilter?.conditions[0])?.includes(null));

  const isIncludingBlanks = curFilter?.conditions?.some(
    condition => 'includeBlanks' in condition
  );

  return (
    <div className="filter-box date-filter">
      <div>
        {field.name}&nbsp;
        <Options
          options={opts}
          onSelect={o => {
            if ([Op.isEmpty, Op.isNotEmpty].includes(o)) {
              setFilter({
                ...curFilter,
                conditions: [{ [o]: true }]
              });
            } else if (o == Op.relative) {
              const conditions: Condition[] = [
                { [o]: { from: TODAY, to: TODAY } }
              ];
              if (isIncludingBlanks) conditions.push({ includeBlanks: true });
              setFilter({
                ...curFilter,
                conditions
              });
            } else {
              let v = val;
              if (isRelative(o)) v = true;
              else if (isRelative(op)) v = null;
              else if (
                o != Op.between &&
                op == Op.between &&
                Array.isArray(v)
              ) {
                v = v[0];
              } else if (
                o == Op.between &&
                op != Op.between &&
                !Array.isArray(v)
              ) {
                v = [v];
              } else if ([Op.equals, Op.greaterThan, Op.lessThan].includes(o)) {
                v = typeof v == 'string' ? v : '';
              }
              const conditions: Condition[] = [{ [o]: v }];
              if (isIncludingBlanks && o != Op.equals)
                conditions.push({ includeBlanks: true });
              setFilter({
                ...curFilter,
                conditions
              });
            }
          }}
          selected={op}
        />
        {[
          Op.relative,
          Op.greaterThan,
          Op.lessThan,
          Op.between,
          Op.P7D,
          Op.P30D,
          Op.P90D
        ].includes(op) && (
          <div className="checkbox-container">
            <input
              type="checkbox"
              className="checkbox"
              checked={!isCheckboxDisabled && isIncludingBlanks}
              disabled={isCheckboxDisabled}
              onChange={() => {
                const conditions: Condition[] = curFilter?.conditions?.filter(
                  condition => !('includeBlanks' in condition)
                );
                if (!isIncludingBlanks)
                  conditions.push({ includeBlanks: true });
                setFilter({
                  ...curFilter,
                  conditions
                });
              }}
            />
            <div>Include Blanks</div>
          </div>
        )}
      </div>
      <div>
        {op == Op.between && ![Op.isEmpty, Op.isNotEmpty].includes(op) && (
          <DatePicker
            selectsRange
            selected={startDate}
            startDate={startDate}
            endDate={endDate}
            onChange={dates => {
              let selDates = [
                formatDate(dates[0], true),
                formatDate(dates[1], true)
              ].filter(x => x);
              const conditions: Condition[] = [{ [op]: selDates }];
              if (isIncludingBlanks) conditions.push({ includeBlanks: true });
              setFilter({
                ...curFilter,
                conditions
              });
            }}
          />
        )}
        {op != Op.between &&
          ![Op.isEmpty, Op.isNotEmpty].includes(op) &&
          !isRelative(op) && (
            <DatePicker
              selected={startDate}
              onChange={date => {
                let v = formatDate(date, true);
                const conditions: Condition[] = [{ [op]: v }];
                if (isIncludingBlanks) conditions.push({ includeBlanks: true });
                setFilter({
                  ...curFilter,
                  conditions
                });
              }}
            />
          )}
        {op == Op.relative && (
          <RelativeRange
            selected={val}
            onChange={debounce(v => {
              const conditions: Condition[] = [{ [op]: v }];
              if (isIncludingBlanks) conditions.push({ includeBlanks: true });
              setFilter({
                ...curFilter,
                conditions
              });
            }, 200)}
          />
        )}
      </div>
    </div>
  );
};

type RR = { from?: string; to?: string };
interface RelativeRangeProps {
  selected: RR;
  onChange: (newVal: RR) => void;
}
export const RelativeRange = (props: RelativeRangeProps) => (
  <div className="relative-range-filter">
    <RelativeRangeItem type="from" {...props} />
    <RelativeRangeItem type="to" {...props} />
  </div>
);
interface RRIProps extends RelativeRangeProps {
  type: 'from' | 'to';
}
const parseRelativeValue = (v: string, isFrom: boolean) => {
  if (typeof v != 'string' || v == TODAY) return { n: 0, d: 'D' };
  const strs = v.split('').filter(x => x != 'P');
  const d = strs.pop();
  const n = parseInt(strs.join(''));
  return {
    n: isNaN(n) ? 0 : isFrom ? -n : n,
    d
  };
};
const DateType = {
  D: 'Days',
  W: 'Weeks',
  M: 'Months',
  Q: 'Quarters'
};
export const formatDateFilterValue = (val: RR) => {
  if (!val) return null;
  let { from, to } = val;
  if (from == TODAY) from = 'Today';
  else {
    const { n, d } = parseRelativeValue(from, true);
    from = `Last ${n} ${DateType[d]}`;
  }
  if (to == TODAY) to = 'Today';
  else {
    const { n, d } = parseRelativeValue(to, false);
    to = `Next ${n} ${DateType[d]}`;
  }
  return from + ' to ' + to;
};
const RelativeRangeItem = ({ type, selected, onChange }: RRIProps) => {
  const value = selected ? selected[type] : null;
  const [state, setState] = React.useState<string>(
    !selected ? null : selected[type]
  );
  const { n, d } = parseRelativeValue(state, type == 'from');
  const [text, setText] = React.useState<string>(n + '');

  const setValue = (v: string, x?: number | string) => {
    if (v != TODAY) {
      setText(x + '');
      if (typeof x == 'string') x = parseInt(x);
      v = 'P' + (type == 'from' ? -x : x) + v;
      setState(v);
    }
    onChange({ ...(selected || {}), [type]: v });
  };
  return (
    <div>
      <div>{type == 'from' ? 'From' : 'To'}</div>
      <div className="form-check">
        <label className="form-check-label">
          <input
            type="radio"
            className="form-check-input"
            name={type + 'Radio'}
            checked={value == TODAY}
            onChange={() => setValue('TODAY')}
          />
          <span>Today</span>
        </label>
        <label className="form-check-label">
          <input
            type="radio"
            className="form-check-input"
            name={type + 'Radio'}
            checked={value && value != TODAY}
            onChange={() => setValue(d, n)}
          />
          <span>{type == 'from' ? 'Last' : 'Next'}</span>
          <input
            type="number"
            value={text}
            className="form-input"
            onFocus={() => {
              if (state == TODAY) setValue(d, n);
            }}
            onChange={e => {
              const t = e.target.value;
              if (t != '' && t != '-') setValue(d, e.target.value);
              else setText(t);
            }}
          />
          <Dropdown
            options={Object.entries(DateType).map(([value, label]) => ({
              value,
              label
            }))}
            onSelect={o => setValue(o.value, n)}
            renderOption={o => (
              <div>
                <span className="dd-text" data-txt={o.label}>
                  {o.label}
                </span>
              </div>
            )}
          >
            <div className="operator">
              {DateType[d]}
              <Arrow direction="down" type="line" />
            </div>
          </Dropdown>
        </label>
      </div>
    </div>
  );
};
